import { Controller } from "@hotwired/stimulus"

const URL = "/components/it_tools/brand_data_management_tool/map_hs_brands_to_brands"

// HS Brand Mapping in the Brand Data Management tool: map one HsBrand, or every HsBrand the
// filters pick. A "many" run always previews first and asks before mapping.
export default class extends Controller {
  static targets = [
    "count", "hsBrandSelect", "scope", "brandsWrapper", "hsBrandMultiSelect", "mappingState",
    "createMissing", "activeWrapper", "active",
    "updatedProperties", "button", "result"
  ]

  connect() {
    this.toggleScope()
    this.toggleCreateMissing()
  }

  toggleScope() {
    this.brandsWrapperTarget.hidden = this.checkedValue(this.scopeTargets) !== "selected"
  }

  // The Active/Not Active choice only matters when brands might get created.
  toggleCreateMissing() {
    this.activeWrapperTarget.hidden = !this.createMissingTarget.checked
  }

  clearResult() {
    this.resultTarget.innerHTML = ""
  }

  mapOne() {
    const hsBrandId = this.hsBrandSelectTarget.value
    if (!hsBrandId) {
      this.resultTarget.innerHTML = this.alert("danger", "Choose a HubSpot brand.")
      return
    }

    this.post({ scope: "one", hs_brand_id: hsBrandId }, "Mapping...").done((data) => this.renderDone(data))
  }

  preview() {
    if (!this.validateMany()) return

    this.post(this.manyParams(true), "Counting matching brands...").done((data) => {
      this.resultTarget.innerHTML = data.success ? this.renderPreview(data) : this.alert("danger", data.errors)
    })
  }

  mapMany() {
    if (!this.validateMany()) return

    this.post(this.manyParams(true), "Counting matching brands...").done((data) => {
      if (!data.success) {
        this.resultTarget.innerHTML = this.alert("danger", data.errors)
        return
      }
      this.resultTarget.innerHTML = this.renderPreview(data)
      if (!data.count) return
      if (!window.confirm("Map " + data.count + " HubSpot brands?")) return

      this.post(this.manyParams(false), "Mapping...").done((mapped) => this.renderDone(mapped))
    })
  }

  manyParams(dryRun) {
    const params = {
      scope: "many",
      brand_scope: this.checkedValue(this.scopeTargets),
      mapping_state: this.checkedValue(this.mappingStateTargets),
      dry_run: dryRun
    }
    if (params.brand_scope === "selected") params.brand_ids = this.selectedValues(this.hsBrandMultiSelectTarget)
    return params
  }

  validateMany() {
    if (this.checkedValue(this.scopeTargets) === "selected" && !this.selectedValues(this.hsBrandMultiSelectTarget).length) {
      this.resultTarget.innerHTML = this.alert("danger", "Choose at least one brand, or pick All brands.")
      return false
    }
    return true
  }

  // The options apply to either kind of run.
  post(params, pendingMessage) {
    const createMissing = this.createMissingTarget.checked
    const options = { create_missing: createMissing }
    if (createMissing) options.active = this.checkedValue(this.activeTargets)

    // Omitted entirely when blank: the server reads a missing value as "sync every property",
    // while an empty list would mean "sync nothing".
    const updatedProperties = this.updatedPropertiesTarget.value.trim()
    if (updatedProperties) options.updated_properties = updatedProperties

    this.buttonTargets.forEach((button) => { button.disabled = true })
    this.resultTarget.innerHTML = '<div class="text-muted">' + pendingMessage + "</div>"

    return $.post(URL, Object.assign(options, params))
      .fail(() => { this.resultTarget.innerHTML = this.alert("danger", "Something went wrong.") })
      .always(() => { this.buttonTargets.forEach((button) => { button.disabled = false }) })
  }

  renderDone(data) {
    this.resultTarget.innerHTML = data.success ? this.alert("success", data.message) : this.alert("danger", data.errors)
  }

  renderPreview(data) {
    if (!data.count) return this.alert("warning", "No HubSpot brands match those filters.")

    let html = this.alert("info", data.count + " HubSpot brands match, " + data.unmapped_count + " not mapped yet.")
    html += '<ul class="small mb-0 mt-2">' + data.sample.map((label) => "<li>" + this.escapeHtml(label) + "</li>").join("")
    if (data.count > data.sample.length) html += '<li class="text-muted">…and ' + (data.count - data.sample.length) + " more</li>"
    return html + "</ul>"
  }

  checkedValue(radios) {
    const checked = radios.find((radio) => radio.checked)
    return checked ? checked.value : null
  }

  // TomSelect keeps the underlying <select> in sync, so its selected options are the truth.
  selectedValues(select) {
    return Array.from(select.selectedOptions).map((option) => option.value).filter(Boolean)
  }

  alert(kind, message) {
    return '<div class="alert alert-' + kind + ' mb-0">' + this.escapeHtml(message || "Something went wrong.") + "</div>"
  }

  escapeHtml(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }
}
